/**
 * Axios 请求封装
 * 支持环境切换、请求拦截、响应拦截、错误处理
 */
import axios from 'axios'
import apiConfig from '@/config/api'
import { Message } from '@arco-design/web-vue'
import router from '@/router'
import store from '@/store'

// 创建 axios 实例
const service = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    
    // 添加 token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = token
    }
    
    // 如果是FormData类型的数据，完全移除Content-Type，让浏览器自动处理
    if (config.data instanceof FormData) {
      config.headers = {
        ...config.headers,
        // 移除所有Content-Type相关设置
      }
      delete config.headers['Content-Type']
      // 确保没有其他可能影响FormData的配置
      config.transformRequest = null
    }
    
    // 开发环境打印请求信息
    if (apiConfig.showLog) {
      console.log('请求配置:', config)
    }
    
    return config
  },
  error => {
    // 对请求错误做些什么
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 直接返回响应数据，但需要检查业务层的token过期
    const data = response.data
    
    if (apiConfig.showLog) {
      console.log('响应数据:', data)
    }
    
    // 检查业务层的token失效（code=500且消息明确指向 token/过期 才登出；
    // 不再匹配宽泛的"登录"，避免如"请先登录"等瞬时业务报错把用户误登出）
    if (data && data.code === 500) {
      const msg = (data.msg || data.message || '').toLowerCase()
      if (msg.includes('token') || msg.includes('过期')) {
        Message.error('登录已过期，请重新登录')
        // 清除所有登录信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('expireTime')
        store.commit('clearAuth')
        // 跳转到登录页
        setTimeout(() => {
          router.push('/login').catch(() => {})
        }, 500)
        return Promise.reject(new Error(data.msg || '登录已过期'))
      }
    }
    
    return data
  },
  error => {
    // 对响应错误做点什么
    
    console.error('响应错误:', error)
    console.error('错误详情:', {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config
    })
    
    let message = '请求失败'
    
    if (error.response) {
      // 服务器返回了错误状态码
      const status = error.response.status
      const responseData = error.response.data
      
      // 尝试从响应数据中获取错误信息
      if (responseData && typeof responseData === 'object') {
        if (responseData.message) {
          message = responseData.message
        } else if (responseData.msg) {
          message = responseData.msg
        } else if (responseData.error) {
          message = responseData.error
        }
      }
      
      // 如果没有从响应数据中获取到错误信息，使用默认的状态码信息
      if (message === '请求失败') {
        switch (status) {
          case 400:
            message = '请求参数错误'
            break
          case 401:
            message = '登录已过期，请重新登录'
            break
          case 403:
            message = '权限不足'
            break
          case 404:
            message = '请求地址不存在'
            break
          case 500:
            message = '服务器内部错误'
            break
          case 502:
            message = '网关错误'
            break
          case 503:
            message = '服务不可用'
            break
          case 504:
            message = '网关超时'
            break
          default:
            message = `请求失败 (${status})`
        }
      }
      
      // 处理401状态码：token过期或无效
      if (status === 401) {
        // 清除所有登录信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('expireTime')
        store.commit('clearAuth')
        // 延迟跳转，确保消息显示
        setTimeout(() => {
          router.push('/login').catch(() => {})
        }, 500)
      }
    } else if (error.request) {
      // 请求已发出，但没有收到响应
      message = '网络连接失败，请检查网络'
    } else {
      // 发送请求时出了点问题
      message = error.message || '请求失败'
    }
    
    // 只在有明确错误信息时才显示错误提示
    if (message !== '请求失败' || error.response) {
      Message.error(message)
    }
    
    return Promise.reject(error)
  }
)

export default service

